import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, CheckCircle, Play, Trophy, AlertCircle } from 'lucide-react';
import "../../../assets/css/gamesCss/Array/sorting1D.css"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";


const Sorting1D = () => {
    const { darkMode } = useDarkMode();
    const [currentLevel, setCurrentLevel] = useState(1);
    const [selectedNodes, setSelectedNodes] = useState([]);
    const [moves, setMoves] = useState(0);
    const [isComplete, setIsComplete] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [lives, setLives] = useState(3);
    const [errorMessage, setErrorMessage] = useState('');
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();


    const levels = {
        1: {
            array: [64, 34, 25, 12, 22, 11],
            target: [11, 12, 22, 25, 34, 64],
            type: 'bubble',
            name: 'Bubble Sort - Εύκολο'
        },
        2: {
            array: [45, 23, 67, 12, 89, 34],
            target: [12, 23, 34, 45, 67, 89],
            type: 'bubble',
            name: 'Bubble Sort - Μέτριο'
        },
        3: {
            array: [38, 27, 43, 3, 9, 82, 10, 55],
            target: [3, 9, 10, 27, 38, 43, 55, 82],
            type: 'insertion',
            name: 'Insertion Sort - Δύσκολο'
        },
        4: {
            array: [72, 41, 63, 15, 28, 97, 54, 8],
            target: [8, 15, 28, 41, 54, 63, 72, 97],
            type: 'insertion',
            name: 'Insertion Sort - Πολύ Δύσκολο'
        }
    };

    const [arrays, setArrays] = useState({
        1: [...levels[1].array],
        2: [...levels[2].array],
        3: [...levels[3].array],
        4: [...levels[4].array]
    });

    const [algorithmSteps, setAlgorithmSteps] = useState({
        1: { i: 0, j: 0, pass: 0, sortedUntil: 6 },
        2: { i: 0, j: 0, pass: 0, sortedUntil: 6 },
        3: { i: 1, key: null, sortedUntil: 1 },
        4: { i: 1, key: null, sortedUntil: 1 }
    });

    const getCurrentArray = () => arrays[currentLevel];
    const getCurrentTarget = () => levels[currentLevel].target;
    const getCurrentType = () => levels[currentLevel].type;


    useEffect(() => {
        if (errorMessage) {
            setTimeout(() => {
                setErrorMessage("")
            }, 2000);
        }
    })


    const setCurrentArray = (newArray) => {
        setArrays(prev => ({
            ...prev,
            [currentLevel]: newArray
        }));
    };

    const isValidBubbleSwap = (idx1, idx2, array) => {
        const smaller = Math.min(idx1, idx2);
        const larger = Math.max(idx1, idx2);
        const step = algorithmSteps[currentLevel];

        // Έλεγχος αν είναι γειτονικά
        if (larger - smaller !== 1) {
            setErrorMessage(selLanguage ? 'Πρόσεχε ποια στοιχεία αλλάζεις!' : "Be careful which elements you swap!");
            return false;
        }

        // Έλεγχος αν το ζευγάρι που επέλεξε ο χρήστης είναι το σωστό (πρέπει να είναι το current j)
        if (smaller !== step.j) {
            setErrorMessage(selLanguage ? "Λάθος ζευγάρι! Στο Bubble Sort πρέπει να ελέγξεις τα ζευγάρια με τη σειρά." : "Wrong pair! In Bubble Sort, you need to check the pairs in order.");
            return false;
        }

        // Έλεγχος αν το ζευγάρι είναι εντός του μη-ταξινομημένου τμήματος
        // valid pairs have larger < sortedUntil
        if (larger >= step.sortedUntil) {
            setErrorMessage(selLanguage ? 'Αυτά τα στοιχεία είναι ήδη στη σωστή τους θέση!' : 'These elements are already in their correct position!');
            return false;
        }

        // Έλεγχος αν το αριστερό είναι μεγαλύτερο από το δεξί (μόνο τότε κάνουμε swap)
        if (array[smaller] <= array[larger]) {
            setErrorMessage(selLanguage ? 'Αυτή η εναλλαγή δεν είναι σωστή!' : "This swap is not correct!");
            return false;
        }

        return true;
    };

    const restartGame = () => {
        setLives(3);
        setShowGameOver(false);
        setShowGameWon(false);
        setCurrentLevel(1);

        // Επαναφορά όλων των πινάκων στην αρχική τους κατάσταση
        setArrays({
            1: [...levels[1].array],
            2: [...levels[2].array],
            3: [...levels[3].array],
            4: [...levels[4].array]
        });

        // Επαναφορά των algorithm steps
        setAlgorithmSteps({
            1: { i: 0, j: 0, pass: 0, sortedUntil: 6 },
            2: { i: 0, j: 0, pass: 0, sortedUntil: 6 },
            3: { i: 1, key: null, sortedUntil: 1 },
            4: { i: 1, key: null, sortedUntil: 1 }
        });

        setMoves(0);
        setIsComplete(false);
        setSelectedNodes([]);
        setErrorMessage('');
    }


    const isValidInsertionSwap = (idx1, idx2, array) => {
        const step = algorithmSteps[currentLevel];
        const currentI = step.i;

        // Έλεγξε ότι ένα από τα δύο indices είναι το τρέχον i
        if (idx1 !== currentI && idx2 !== currentI) {
            setErrorMessage(selLanguage ? `Πρόσεχε ποιά στοιχεία εναλλάσεις.` : "Be careful which elements you swap.");
            return false;
        }

        const currentPos = idx1 === currentI ? idx1 : idx2;
        const otherPos = idx1 === currentI ? idx2 : idx1;

        // Μπορούμε να μετακινήσουμε μόνο προς τα αριστερά (otherPos < currentPos)
        if (otherPos >= currentPos) {
            setErrorMessage(selLanguage ? 'Να θυμάσαια την φορά που πρέπει να μετακινείς τα στοιχεία.' : "Remember the direction in which you should move the elements.");
            return false;
        }

        // Πρέπει να είναι γειτονικά (μία θέση κάθε φορά)
        if (currentPos - otherPos !== 1) {
            setErrorMessage(selLanguage ? 'Προσοχή! Θυμήσου τα βήματα που θα ακολουθούσε ο αλγόριθμος.' : "Careful! Remember the steps the algorithm would follow.");
            return false;
        }

        // Το τρέχον στοιχείο πρέπει να είναι μικρότερο από το αριστερό για να κάνουμε swap
        if (array[currentPos] >= array[otherPos]) {
            setErrorMessage(selLanguage ? 'Αυτή η εναλλαγή δεν είναι σωστή! Συνέχισε την καλή προσπάθεια.' : "This swap is not correct! Keep up the good effort.");
            return false;
        }

        return true;
    };

    // ...existing code...
    const handleNodeClick = (index) => {
        setErrorMessage(''); // Καθαρισμός μηνύματος λάθους

        // snapshot για αποφυγή race conditions με setState
        const curSelected = [...selectedNodes];
        const type = getCurrentType();
        const step = algorithmSteps[currentLevel];
        const array = [...getCurrentArray()];

        // Αν υπάρχουν ήδη δύο επιλεγμένα - καθαρίζουμε (προστασία)
        if (curSelected.length >= 2) {
            setSelectedNodes([]);
            return;
        }

        // Εάν δεν υπάρχει επιλεγμένο -> απλώς επιλέγουμε το στοιχείο
        if (curSelected.length === 0) {
            setSelectedNodes([index]);
            return;
        }

        // Υπάρχει ήδη ένα επιλεγμένο στοιχείο
        const firstIndex = curSelected[0];

        if (firstIndex === index) {
            if (type === 'bubble') {
                // Skip logic: επιτρέπουμε skip μόνο αν είμαστε στο σωστό j και ΔΕΝ χρειάζεται swap
                if (firstIndex !== step.j) {
                    setErrorMessage(selLanguage ? `Σκέψου προσεκτικά ποιό θα ήταν το επόμενο βήμα του αλγορίθμου.` : "Think carefully about what the next step of the algorithm would be.");
                    setSelectedNodes([]);
                    return;
                }

                if (array[firstIndex] <= array[firstIndex + 1]) {
                    const nextJ = step.j + 1;
                    const sortedUntil = step.sortedUntil || array.length;
                    if (nextJ >= sortedUntil - 1) {
                        // Τέλος περάσματος
                        setAlgorithmSteps(prev => ({
                            ...prev,
                            [currentLevel]: {
                                j: 0,
                                sortedUntil: Math.max(1, sortedUntil - 1),
                                pass: (step.pass || 0) + 1
                            }
                        }));
                    } else {
                        setAlgorithmSteps(prev => ({
                            ...prev,
                            [currentLevel]: {
                                ...step,
                                j: nextJ
                            }
                        }));
                    }
                    setMoves(m => m + 1);
                    setSelectedNodes([]);
                    return;
                } else {
                    setErrorMessage(selLanguage ? ' Αυτό το ζευγάρι ΧΡΕΙΑΖΕΤΑΙ εναλλαγή! Επίλεξε και τα δύο στοιχεία για να τα αλλάξεις.' : ' This pair NEEDS a swap! Select both elements to swap them.');
                    setSelectedNodes([]);
                    return;
                }
            }
            else if (type === 'insertion') {
                const step = algorithmSteps[currentLevel];
                const array = getCurrentArray();
                const i = step.i;

                // αν δεν χρειάζεται να κινηθεί (array[i] >= array[i - 1] ή i === 0)
                if (i === 0 || array[i] >= array[i - 1]) {
                    const nextI = Math.min(i + 1, array.length - 1);
                    setAlgorithmSteps(prev => ({
                        ...prev,
                        [currentLevel]: {
                            i: nextI,
                            key: null,
                            sortedUntil: nextI
                        }
                    }));
                    setMoves(m => m + 1);
                } else {
                    setErrorMessage(
                        selLanguage
                            ? 'Αυτό το στοιχείο πρέπει να κινηθεί προς τα αριστερά!'
                            : 'This element needs to move left!'
                    );
                }

                setSelectedNodes([]);
                return;
            }

            setSelectedNodes([]);
            return;
        }

        const secondIndex = index;


        if (type === 'bubble') {
            const smaller = Math.min(firstIndex, secondIndex);
            const larger = Math.max(firstIndex, secondIndex);
            const sortedUntil = step.sortedUntil || array.length;
            if (smaller === step.j && larger === step.j + 1 && array[smaller] <= array[larger]) {
                const nextJ = step.j + 1;
                if (nextJ >= sortedUntil - 1) {
                    setAlgorithmSteps(prev => ({
                        ...prev,
                        [currentLevel]: {
                            j: 0,
                            sortedUntil: Math.max(1, sortedUntil - 1),
                            pass: (step.pass || 0) + 1
                        }
                    }));
                } else {
                    setAlgorithmSteps(prev => ({
                        ...prev,
                        [currentLevel]: {
                            ...step,
                            j: nextJ
                        }
                    }));
                }
                setMoves(m => m + 1);
                setSelectedNodes([]);
                return;
            }
        }

        let isValid = false;
        if (type === 'bubble') {
            isValid = isValidBubbleSwap(firstIndex, secondIndex, array);
        } else {
            isValid = isValidInsertionSwap(firstIndex, secondIndex, array);
        }

        if (!isValid) {

            setSelectedNodes([]);
            const newLives = lives - 1;
            setLives(newLives);
            if (newLives === 0) {
                setShowGameOver(true);
            }
            return;
        }

        // Έγκυρη εναλλαγή -> κάνουμε swap και ενημερώνουμε array
        [array[firstIndex], array[secondIndex]] = [array[secondIndex], array[firstIndex]];
        setCurrentArray(array);
        setSelectedNodes([]);
        setMoves(m => m + 1);

        // Ενημέρωση βημάτων ανά αλγόριθμο
        if (type === 'bubble') {
            const s = step;
            const nextJ = (s.j || 0) + 1;
            const sortedUntil = s.sortedUntil || array.length;
            if (nextJ >= sortedUntil - 1) {
                setAlgorithmSteps(prev => ({
                    ...prev,
                    [currentLevel]: {
                        j: 0,
                        sortedUntil: Math.max(1, sortedUntil - 1),
                        pass: (s.pass || 0) + 1
                    }
                }));
            } else {
                setAlgorithmSteps(prev => ({
                    ...prev,
                    [currentLevel]: {
                        ...s,
                        j: nextJ
                    }
                }));
            }
        } else if (type === 'insertion') {
            const s = step;
            // Το μετακινούμενο στοιχείο μετά το swap έχει θέση newPos (το μικρότερο index)
            const newPos = Math.min(firstIndex, secondIndex);
            // Αν χρειάζεται ακόμη να μετακινηθεί αριστερά, κρατάμε το ίδιο i = newPos
            if (newPos > 0 && array[newPos] < array[newPos - 1]) {
                setAlgorithmSteps(prev => ({
                    ...prev,
                    [currentLevel]: {
                        ...s,
                        i: newPos
                    }
                }));
            } else {
                // Το στοιχείο μπήκε στη σωστή θέση -> προχωράμε στο επόμενο i
                const nextI = Math.min((s.i || 1) + 1, array.length - 1);
                setAlgorithmSteps(prev => ({
                    ...prev,
                    [currentLevel]: {
                        i: nextI,
                        key: null,
                        sortedUntil: nextI
                    }
                }));
            }
        }
    };

    const checkCompletion = () => {
        const current = getCurrentArray();
        const target = getCurrentTarget();
        const isCorrect = JSON.stringify(current) === JSON.stringify(target);

        if (isCorrect) {
            setIsComplete(true);
            if (currentLevel === 4) {
                setShowGameWon(true);
            }
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2000);
        } else {
            const newLives = lives - 1;
            setLives(newLives);
            if (newLives === 0) {
                setShowGameOver(true);
            }
            setErrorMessage(selLanguage ? 'Ο πίνακας δεν είναι ακόμα σωστά ταξινομημένος! Συνέχισε την προσπάθεια! Πρόσεχε όμως, σου απομένουν ' + newLives + " ζωές" : "The array is not sorted correctly yet! Keep trying! But be careful, you have " + newLives + " lives left");

        }

        return isCorrect;
    };

    const resetLevel = () => {
        setArrays(prev => ({
            ...prev,
            [currentLevel]: [...levels[currentLevel].array]
        }));
        setSelectedNodes([]);
        setMoves(0);
        setIsComplete(false);
        setLives(3);
        setErrorMessage('');

        if (levels[currentLevel].type === 'insertion') {
            setAlgorithmSteps(prev => ({
                ...prev,
                [currentLevel]: { i: 1, key: null, sortedUntil: 1 }
            }));
        } else {
            const arrayLength = levels[currentLevel].array.length;
            setAlgorithmSteps(prev => ({
                ...prev,
                [currentLevel]: { i: 0, j: 0, pass: 0, sortedUntil: arrayLength }
            }));
        }
    };

    const nextLevel = () => {
        if (currentLevel < 4) {
            setCurrentLevel(currentLevel + 1);
            setMoves(0);
            setIsComplete(false);
            setSelectedNodes([]);
            setLives(3);
            setErrorMessage('');
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: "spring", stiffness: 100 }
        }
    };

    const getBoxStyle = (index) => {
        const type = getCurrentType();
        const step = algorithmSteps[currentLevel];
        const isSelected = selectedNodes.includes(index);

        let borderColor = 'rgba(255, 255, 255, 0.3)';
        let borderWidth = '2px';


        if (isSelected) {
            borderColor = '#bdbdbdff';
            borderWidth = '3px';
        }
        return {
            width: '60px',
            height: '60px',
            background: isSelected ? 'linear-gradient(45deg, #080f75ff, #030a72ff)' : 'linear-gradient(45deg, #3b82f6, #1d4ed8)',
            border: `${borderWidth} solid ${borderColor}`,
            borderRadius: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.3rem',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
        };
    };

    return (
        <div
            className='main-content sorting1DContainer'
            style={{
                background: darkMode ? "#1E293B" : "var(--customBuildButton)",
            }}
        >

            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={restartGame}

            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={restartGame}

            />

            <div style={{ maxWidth: '1200px', width: '100%' }}>
                {/* Header με Lives και Level */}
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: 'wrap', gap: '15px' }}>
                    {/* Level Info */}
                    <div style={{
                        color: darkMode ? "white" : "#1E293B",
                        padding: "12px 24px",
                        borderRadius: "12px",
                        fontWeight: "700",
                        fontSize: "18px",
                        border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                        {selLanguage ? `Επίπεδο ${currentLevel}/4` : `Level ${currentLevel}/4`}
                    </div>

                    {/* Lives Display */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                    }}>

                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Instructions */}
                <motion.div
                    className='sorting1DPron'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}

                >
                    <p style={{ margin: 0 }}>
                        {levels[currentLevel].type === 'bubble'
                            ? (
                                selLanguage
                                    ? ' Ταξινόμησε τον πίνακα όπως θα έκανε ακριβώς ο αλγόριθμος Bubble Sort! Πρόσεχε ποιά στοιχεία εναλλάσεις. Δεν μπορείς να κάνεις αυθαίρετες κινήσεις γιατί θα χάσεις ζωές.'
                                    : ' Sort the array exactly as the Bubble Sort algorithm would! Be careful which elements you swap. You cannot make arbitrary moves or you will lose lives.'
                            )
                            : (
                                selLanguage
                                    ? ' Εφάρμοσε τα βήματα του Insertion Sort: σε κάθε βήμα μετακίνησε το σωστό στοιχείο προς τα αριστερά, μία θέση τη φορά, μέχρι να μπει στη σωστή του θέση. Αν πιστεύεις ότι το στοιχείο είναι ήδη στη σωστή θέση και ΔΕΝ χρειάζεται να μετακινηθεί, κάνε διπλό κλικ πάνω του για να προχωρήσεις στο επόμενο βήμα.'
                                    : ' Apply the steps of Insertion Sort: at each step, move the correct element to the left, one position at a time, until it reaches its correct place. If you think the element is already in the correct position and does NOT need to move, double-click on it to go to the next step.'
                            )
                        }
                    </p>

                </motion.div>

                <div style={{ textAlign: 'center' }}>
                    {/* Target Array */}



                    {/* Current Array */}
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        style={{ marginBottom: '20px' }}
                    >
                        <h3 style={{ color: darkMode ? 'white' : '#1E293B', marginBottom: '20px', fontSize: '1.3rem' }}>
                            {selLanguage ? "Τρέχων Πίνακας" : "Current Array"}
                        </h3>
                        <div style={{
                            display: 'inline-block',
                            padding: '25px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '3px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '20px',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '15px',
                                flexWrap: 'wrap'
                            }}>
                                {getCurrentArray().map((value, index) => (
                                    <motion.div
                                        key={index}
                                        variants={itemVariants}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => handleNodeClick(index)}
                                        style={getBoxStyle(index)}
                                    >
                                        {value}
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* Error Message */}
                    <AnimatePresence>
                        {errorMessage && (
                            <motion.div
                                initial={{ opacity: 0, y: -10, scale: 0.9 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.9 }}
                                style={{
                                    maxWidth: '700px',
                                    margin: '0 auto 20px',
                                    padding: '15px 20px',
                                    background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                                    border: `2px solid "#ef4444"`,
                                    borderRadius: '15px',
                                    color: "#dc2626",
                                    fontSize: '0.95rem',
                                    lineHeight: '1.5',
                                    boxShadow: '0 4px 20px rgba(239, 68, 68, 0.4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    display: "flex",
                                    alignContent: "center",
                                    justifyContent: "center"
                                }}
                            >
                                <div style={{ textAlign: 'left' }}>
                                    {errorMessage}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Controls */}
                    <motion.div
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '15px',
                            marginBottom: '20px',
                            flexWrap: 'wrap'
                        }}
                    >

                        <button
                            className='sorting1DCheck'
                            onClick={checkCompletion}
                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                            {selLanguage ? "Έλεγχος" : "Check"}
                        </button>

                        <button
                            onClick={resetLevel}
                            className='sortin1DRestart'
                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                        >

                            {selLanguage ? "Επαναφορά" : "Reset"}
                        </button>

                    </motion.div>

                    {/* Next Level Button */}
                    <AnimatePresence>
                        {isComplete && (
                            <motion.button
                                onClick={nextLevel}
                                style={{
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '15px',
                                    padding: '15px 30px',
                                    fontSize: '1.2rem',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    margin: '0 auto',
                                    boxShadow: '0 8px 15px rgba(18, 20, 156, 0.3)'
                                }}
                            >
                                {currentLevel < 4 ? selLanguage ? 'Επόμενο Επίπεδο' : 'Next Level'
                                    : selLanguage ? "Ολοκλήρωσες το Παιχνίδι! " : "You completed the game!"}
                            </motion.button>
                        )}
                    </AnimatePresence>


                    {/* Success Animation */}
                    <AnimatePresence>
                        {showSuccess && (
                            <motion.div
                                initial={{ opacity: 0, y: -10, scale: 0.9 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.9 }}
                                style={{
                                    maxWidth: '700px',
                                    margin: '0 auto 20px',
                                    padding: '15px 20px',
                                    background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                                    border: `2px solid "#22c55e"`,
                                    borderRadius: '15px',
                                    color: "#059669",
                                    fontSize: '0.95rem',
                                    lineHeight: '1.5',
                                    boxShadow: '0 4px 20px rgba(2, 104, 2, 0.4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    display: "flex",
                                    alignContent: "center",
                                    justifyContent: "center",
                                    marginTop: "20px"
                                }}
                            >
                                {selLanguage ? "Μπράβο!" : "Well done!"}

                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div >
    );
};

export default Sorting1D;